import type { AssetProfileSummary, ProfileMarketSnapshot } from '@akem/shared';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Divider, Grid, InputAdornment, List, ListItem, ListItemText, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkline } from '../../components/Sparkline.js';
import { LogoImage } from '../../components/LogoImage.js';
import { ProfileExposurePanel } from './components/ProfileExposurePanel.js';
import { getMarketSnapshots, listTopProfiles, searchOrCreateProfile } from '../../services/profile.js';

export const ProfilesPage: React.FC = () => {
  const [query, setQuery] = React.useState('AAPL');
  const navigate = useNavigate();

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['profiles-search', query],
    queryFn: async () => {
      if (!query) return null;
      return searchOrCreateProfile(query);
    },
    enabled: false,
  });

  const { data: topList } = useQuery({
    queryKey: ['profiles-top'],
    queryFn: async () => listTopProfiles({ useMock: true }),
  });

  const { data: snapshots } = useQuery({
    queryKey: ['profiles-top-snapshots', topList?.map(p => p.id).join(',')],
    enabled: (topList?.length || 0) > 0,
    queryFn: async () => {
      const ids = (topList || []).map(p => p.id);
      return getMarketSnapshots(ids, 60);
    },
    refetchInterval: 60000, // poll every minute while visible
  });

  const candidateList: Array<{ ticker: string; name: string; confidence?: number }> = data?.needsDisambiguation ? (data.candidates || []) : [];
  const profile: AssetProfileSummary | undefined = !data?.needsDisambiguation ? (data?.profile as any) : undefined;

  const timeAgo = (iso?: string | null) => {
    if (!iso) return '';
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 2, overflow: 'hidden' }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h6">Profiles</Typography>
        {/* Hide generic canvas entry; users navigate to Market or Profile canvases only */}
        <Button size="small" onClick={() => navigate('/market')} sx={{ ml: 1 }}>Market</Button>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search ticker or company (e.g., AAPL, Apple)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') refetch(); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ minWidth: 360 }}
        />
        <Button variant="contained" onClick={() => refetch()} startIcon={<SearchIcon />}>Search</Button>
      </Stack>

      <Grid container spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Primary list column */}
        <Grid item xs={12} md={8} sx={{ height: '100%' }}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardHeader title="Top Profiles" subheader="" />
            <Divider />
            <CardContent sx={{ flex: 1, overflow: 'auto' }}>
              {!topList && (
                <Stack spacing={1}>
                  <Skeleton width={220} />
                  <Skeleton width={260} />
                  <Skeleton width={240} />
                </Stack>
              )}
              {topList && topList.map((p) => {
                const snap = (snapshots || ({} as Record<string, ProfileMarketSnapshot | null>))[p.id];
                const price = snap?.price ?? 0;
                const spark = snap?.sparkline ?? [];
                const change = snap?.changePercent ?? 0;
                return (
                  <Box
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/canvas?profileId=${encodeURIComponent(p.id)}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/canvas?profileId=${encodeURIComponent(p.id)}`); } }}
                    onMouseMove={(e) => {
                      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      (e.currentTarget as HTMLElement).style.setProperty('--mx', `${e.clientX - r.left}px`);
                      (e.currentTarget as HTMLElement).style.setProperty('--my', `${e.clientY - r.top}px`);
                    }}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      p: 1.25,
                      borderRadius: 2,
                      border: '1px solid rgba(255,255,255,0.06)',
                      cursor: 'pointer',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
                      overflow: 'hidden',
                      transition: 'border-color 150ms ease, box-shadow 150ms ease, background 150ms ease',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.14)',
                        boxShadow: '0 12px 40px -16px rgba(116,6,242,0.45)',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(140px circle at var(--mx, -100px) var(--my, -100px), rgba(116,6,242,0.18), transparent 60%)',
                        opacity: 0,
                        transition: 'opacity 150ms ease',
                        pointerEvents: 'none',
                      },
                      '&:hover::after': {
                        opacity: 1,
                      },
                    }}
                  >
                    <LogoImage
                      src={p.logoUrl}
                      alt={p.ticker}
                      size={24}
                      fallbackText={(p.ticker || p.name || '?')}
                      sx={{ mr: 1 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{p.name} ({p.ticker})</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{[p.sector, p.industry].filter(Boolean).join(' • ')}</Typography>
                    </Box>
                    {/* Right cluster: price + sparkline + change (fixed widths for alignment) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 300 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontVariantNumeric: 'tabular-nums', 
                          width: 96, 
                          textAlign: 'right' 
                        }}
                      >
                        {price ? `$${price.toFixed(2)}` : '—'}
                      </Typography>
                      <Sparkline data={spark.length ? spark : [100,101,99,102,101,103,104]} width={120} height={24} />
                      <Chip 
                        size="small" 
                        variant="outlined" 
                        color={change >= 0 ? 'success' : 'error'} 
                        label={`${change >= 0 ? '+' : ''}${change.toFixed(2)}%`} 
                        sx={{ 
                          width: 96,
                          fontVariantNumeric: 'tabular-nums',
                          '& .MuiChip-label': { 
                            width: '100%', 
                            display: 'inline-flex',
                            justifyContent: 'center'
                          }
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>
        {/* Compact search results column (only when results exist) */}
        <Grid item xs={12} md={4}>
          {(isFetching || data) && (
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardHeader title="Search Result" subheader="Basics render instantly; enrichment is queued if new" />
              <Divider />
              <CardContent sx={{ flex: 1, overflow: 'auto' }}>
                {!data && isFetching && (
                  <Stack spacing={1}><Skeleton width={240} /><Skeleton width={380} /></Stack>
                )}
                {profile && (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LogoImage
                        src={profile.logoUrl}
                        alt={profile.ticker}
                        size={24}
                        fallbackText={(profile.ticker || profile.name || '?')}
                      />
                      <Typography variant="subtitle1">{profile.name} ({profile.ticker})</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      {profile.sector && <Chip label={profile.sector} size="small" />}
                      {profile.industry && <Chip label={profile.industry} size="small" />}
                      <Chip label={`Research ${profile.researchCompleteness}%`} size="small" />
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" onClick={() => navigate(`/canvas?profileId=${encodeURIComponent(profile.id)}`)}>Explore</Button>
                    </Stack>
                    {/* Exposures & Filings panel */}
                    <ProfileExposurePanel
                      profileId={(profile as any)?.id}
                      ticker={(profile as any)?.ticker}
                      instrumentId={(profile as any)?.instrumentId}
                    />
                  </Stack>
                )}
                {candidateList.length > 0 && (
                  <List dense>
                    {candidateList.map((c) => (
                      <ListItem key={c.ticker} secondaryAction={
                        <Button size="small" onClick={() => navigate(`/canvas?ticker=${encodeURIComponent(c.ticker)}`)}>Add</Button>
                      }>
                        <ListItemText primary={`${c.name} (${c.ticker})`} secondary={c.confidence != null ? `Confidence ${Math.round((c.confidence || 0)*100)}%` : undefined} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
